"use client";
import { Trash } from "@layer/icons";
import { Badge, Button } from "@layer/ui/components";
import { ColumnHeader, ConfirmationDialog } from "@layer/ui/patterns";
import type { Column, ColumnDef, Row } from "@tanstack/react-table";
import type { MerchantDTO } from "@/api/types/merchants.types";

type GetSelectedMerchantsColumnsParams = {
	t: any;
	onRemove: (merchantId: string) => void;
};

export const getSelectedMerchantsColumns = ({
	t,
	onRemove,
}: GetSelectedMerchantsColumnsParams): ColumnDef<MerchantDTO>[] => [
	{
		accessorKey: "merchantName",
		id: "name",
		header: ({ column }: { column: Column<MerchantDTO> }) => (
			<ColumnHeader column={column} title={t("merchants.table.name")} />
		),
		cell: ({ row }: { row: Row<MerchantDTO> }) => (
			<div className="flex items-center gap-2 text-sm">
				{row.original.merchantLogo && (
					<img
						src={row.original.merchantLogo}
						alt={row.original.merchantName}
						className="size-6 rounded object-cover"
					/>
				)}
				{row.original.merchantName}
			</div>
		),
	},
	{
		accessorKey: "merchantId",
		id: "merchantId",
		header: ({ column }: { column: Column<MerchantDTO> }) => (
			<ColumnHeader
				column={column}
				title={t("merchants.table.merchantId")}
			/>
		),
		cell: ({ row }: { row: Row<MerchantDTO> }) => (
			<span className="text-sm">{row.original.merchantId}</span>
		),
	},
	{
		accessorKey: "status",
		id: "status",
		header: ({ column }: { column: Column<MerchantDTO> }) => (
			<ColumnHeader column={column} title={t("merchants.table.status")} />
		),
		cell: ({ row }: { row: Row<MerchantDTO> }) => (
			<Badge className="capitalize">
				{row.original.status.toLowerCase()}
			</Badge>
		),
	},
	{
		id: "actions",
		size: 100,
		header: () => <></>,
		cell: ({ row }: { row: Row<MerchantDTO> }) => (
			<div className="w-full flex justify-end pr-2">
				<ConfirmationDialog
					trigger={
						<Button variant="ghost" size="tableAction">
							<Trash className="size-4" />
						</Button>
					}
					title={t("merchants.deleteDialog.title")}
					description={t("merchants.deleteDialog.description", {
						merchantName: row.original.merchantName,
					})}
					cancelButtonText={t("merchants.deleteDialog.cancel")}
					confirmationButtonText={t("merchants.deleteDialog.confirm")}
					onConfirm={() => onRemove(row.original.merchantId)}
					variant={"destructive"}
				/>
			</div>
		),
	},
];
